import { useEffect, useState, type ReactNode } from 'react'
import {
  Avatar, Box, Button, CloseButton, Dialog, Drawer, HStack, Input, Portal, Spinner, Text, VStack,
} from '@chakra-ui/react'
import { toaster } from '@/components/ui/toaster'
import { ResponsiveModal } from '../../../shared'
import { useIsMobile } from '../../../../hooks/useIsMobile'
import { useAvailableCanvassers } from '../../../../hooks/useCampaign'
import { useAssignCallBatch } from '../../../../hooks/useFollowUpCalls'
import { extractApiMessage } from '../../../../lib/apiError'
import { toLocalDateStr } from '../routes/routeDates'
import type { FollowUpCallBatchDto } from '../../../../types/followUp'

interface AssignCallBatchSheetProps {
  isOpen: boolean
  onClose: () => void
  campaignId: string
  batch: FollowUpCallBatchDto | null
}

/** Assigns (or reassigns) a call list to a canvasser on a day. Bottom sheet on phone, modal on desktop. */
export function AssignCallBatchSheet({ isOpen, onClose, campaignId, batch }: AssignCallBatchSheetProps) {
  const isMobile = useIsMobile()
  const [date, setDate] = useState(() => batch?.scheduledDate ?? toLocalDateStr(new Date()))
  const [grantId, setGrantId] = useState<string | null>(null)

  // Each open starts from the batch's current assignment.
  useEffect(() => {
    if (!isOpen) return
    setGrantId(batch?.assignedGrantId ?? null)
    setDate(batch?.scheduledDate ?? toLocalDateStr(new Date()))
  }, [isOpen, batch?.id, batch?.assignedGrantId, batch?.scheduledDate])

  const { data: available, isLoading: canvassersLoading } = useAvailableCanvassers(campaignId, isOpen && date ? date : undefined)
  const canvassers = available?.canvassers ?? []

  const { mutate: assign, isPending } = useAssignCallBatch(campaignId)

  const handleSave = () => {
    if (!batch || !grantId) return
    assign(
      { batchId: batch.id, assignedGrantId: grantId, scheduledDate: date || null },
      {
        onSuccess: () => {
          toaster.create({ title: `${batch.label} assigned`, type: 'success', duration: 3000 })
          onClose()
        },
        onError: (err) => {
          toaster.create({ title: 'Could not assign call list', description: extractApiMessage(err, 'Please try again.'), type: 'error', duration: 7000 })
        },
      },
    )
  }

  const title = `Assign ${batch?.label ?? 'call list'}`

  const body: ReactNode = (
    <VStack gap={4} align="stretch" py={2}>
      <Box>
        <Text asChild fontSize="xs" fontWeight="semibold" color="muted" mb={1} display="block">
          <label htmlFor="assign-batch-date">Date</label>
        </Text>
        <Input id="assign-batch-date" type="date" minH="44px" value={date} onChange={(e) => setDate(e.target.value)} />
      </Box>
      <Box>
        <Text fontSize="xs" fontWeight="semibold" color="muted" mb={1}>Canvasser</Text>
        {canvassersLoading ? (
          <VStack py={4}><Spinner color="accent" /></VStack>
        ) : canvassers.length === 0 ? (
          <Text fontSize="sm" color="muted">No canvassers available that day.</Text>
        ) : (
          <VStack gap={1} align="stretch">
            {canvassers.map(c => {
              const picked = grantId === c.grantId
              return (
                <HStack
                  key={c.grantId}
                  p={2}
                  minH="48px"
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={picked ? 'accent' : 'border'}
                  bg={picked ? 'accent.soft' : 'surface'}
                  aria-pressed={picked}
                  aria-label={c.fullName}
                  asChild
                >
                  <button type="button" onClick={() => setGrantId(c.grantId)}>
                    <Avatar.Root size="xs"><Avatar.Fallback name={c.fullName} /><Avatar.Image src={c.portraitUrl ?? undefined} /></Avatar.Root>
                    <Text fontSize="sm">{c.fullName}</Text>
                  </button>
                </HStack>
              )
            })}
          </VStack>
        )}
      </Box>
    </VStack>
  )

  const footer: ReactNode = (
    <HStack gap={3} justify="flex-end" w="100%">
      <Button variant="ghost" minH="44px" onClick={onClose}>Cancel</Button>
      <Button colorPalette="brand" minH="44px" loading={isPending} disabled={!grantId || !batch} onClick={handleSave}>
        Save
      </Button>
    </HStack>
  )

  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} placement="bottom" onOpenChange={e => { if (!e.open) onClose() }}>
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content borderTopRadius="xl" maxH="85vh" pb="env(safe-area-inset-bottom, 0px)">
              <Drawer.CloseTrigger asChild><CloseButton size="sm" /></Drawer.CloseTrigger>
              <Drawer.Header borderBottomWidth="1px">{title}</Drawer.Header>
              <Drawer.Body>{body}</Drawer.Body>
              <Drawer.Footer borderTopWidth="1px">{footer}</Drawer.Footer>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>
    )
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content>
          <Dialog.Header>{title}</Dialog.Header>
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Body>{body}</Dialog.Body>
          <Dialog.Footer>{footer}</Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
